'use client';

import { motion } from 'framer-motion';
import { ReactNode } from 'react';

interface Column {
  key: string;
  label: string;
  className?: string;
}

interface AnimatedTableProps {
  columns: Column[];
  children: ReactNode;
  loading?: boolean;
  compact?: boolean;
  variant?: 'default' | 'gradient' | 'dark' | 'colorful';
}

const headerVariants = {
  default: 'bg-gray-50 border-b border-gray-200',
  gradient: 'bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border-b border-indigo-100',
  dark: 'bg-gradient-to-r from-slate-800 to-slate-700 border-b border-slate-600',
  colorful: 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500',
};

const textVariants = {
  default: 'text-gray-600',
  gradient: 'text-indigo-700',
  dark: 'text-white',
  colorful: 'text-white',
};

export function AnimatedTable({ columns, children, loading, compact = true, variant = 'gradient' }: AnimatedTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-full">
        <thead>
          <tr className={headerVariants[variant]}>
            {columns.map((col, index) => (
              <motion.th
                key={col.key}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`
                  px-3 ${compact ? 'py-3' : 'py-4'} 
                  text-left text-[11px] font-bold uppercase tracking-wider
                  ${textVariants[variant]}
                  ${col.className || ''}
                `}
              >
                <span className={`
                  inline-flex items-center gap-1.5
                  ${variant === 'colorful' ? 'drop-shadow-sm' : ''}
                `}>
                  {col.label}
                </span>
              </motion.th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {children}
        </tbody>
      </table>
    </div>
  );
}

interface AnimatedTableRowProps {
  children: ReactNode;
  index?: number;
  onClick?: () => void;
}

export function AnimatedTableRow({ children, index = 0, onClick }: AnimatedTableRowProps) {
  return (
    <motion.tr
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.02, duration: 0.2 }}
      whileHover={{ backgroundColor: 'rgba(249, 250, 251, 0.8)' }}
      onClick={onClick}
      className={`transition-colors ${onClick ? 'cursor-pointer' : ''}`}
    >
      {children}
    </motion.tr>
  );
}

export function TableCell({ children, className = '' }: { children: ReactNode; className?: string }) {
  // Ensure width classes are properly applied
  const classes = className.split(' ').filter(Boolean);
  const hasWidth = classes.some(c => c.startsWith('w-') || c.startsWith('!w-'));
  
  return (
    <td className={`px-3 py-2.5 text-sm ${className} ${hasWidth ? '' : ''}`}>
      {children}
    </td>
  );
}
